"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import type { CatalogCompany } from "@/lib/companies";
import CategoryBadgePills, { type BadgeItem } from "@/components/CategoryBadgePills";
import PageHero from "@/app/components/PageHero";
import CompanyMatcher from "@/app/components/CompanyMatcher";

export type FilterLeaf = { slug: string; name: string };
export type FilterGroup = { slug: string; name: string; icon?: string | null; children: FilterLeaf[] };
export type FilterAxis = { slug: string; name: string; groups: FilterGroup[] };

const AXIS_KEYS = ["activity", "industry", "tech", "model"] as const;
const DIMENSION_ORDER: Record<string, number> = { activity: 0, industry: 1, tech: 2, model: 3 };
const PAGE_SIZE = 60;

export default function CompaniesCatalog({
  companies,
  axes,
  locale,
  initialSelected,
  initialQuery,
  initialLimit,
}: {
  companies: CatalogCompany[];
  axes: FilterAxis[];
  locale: string;
  initialSelected: Record<string, string[]>;
  initialQuery: string;
  initialLimit: number;
}) {
  const [selected, setSelected] = useState<Record<string, string[]>>(initialSelected);
  const [q, setQ] = useState(initialQuery);
  const [limit, setLimit] = useState(initialLimit);
  const [open, setOpen] = useState(false);

  const meta = useMemo(() => {
    const name = new Map<string, string>();
    const icon = new Map<string, string | null>();
    const dimension = new Map<string, string>();
    const axisSets = new Map<string, Map<string, Set<string>>>();
    for (const axis of axes) {
      const map = new Map<string, Set<string>>();
      for (const group of axis.groups) {
        name.set(group.slug, group.name);
        icon.set(group.slug, group.icon ?? null);
        dimension.set(group.slug, axis.slug);
        map.set(group.slug, new Set([group.slug, ...group.children.map((c) => c.slug)]));
        for (const child of group.children) {
          name.set(child.slug, child.name);
          dimension.set(child.slug, axis.slug);
          map.set(child.slug, new Set([child.slug]));
        }
      }
      axisSets.set(axis.slug, map);
    }
    return { name, icon, dimension, axisSets };
  }, [axes]);

  const activeSets = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    for (const key of AXIS_KEYS) {
      const map = meta.axisSets.get(key);
      const set = new Set<string>();
      if (map) {
        for (const slug of selected[key] ?? []) {
          const expanded = map.get(slug);
          if (expanded) for (const value of expanded) set.add(value);
        }
      }
      out[key] = set;
    }
    return out;
  }, [meta, selected]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hasCategoryFilters = AXIS_KEYS.some((key) => (selected[key] ?? []).length > 0);
    return companies.filter((company) => {
      if (hasCategoryFilters) {
        const hitsFilter = AXIS_KEYS.some((key) => {
          const set = activeSets[key];
          return set.size > 0 && company.categorySlugs.some((s) => set.has(s));
        });
        if (!hitsFilter) return false;
      }
      if (needle) {
        const haystack = `${company.slug} ${company.translations
          .map((t) => `${t.name} ${t.description ?? ""}`)
          .join(" ")}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [companies, activeSets, selected, q]);

  const visible = filtered.slice(0, limit);
  const hasMore = filtered.length > visible.length;

  useEffect(() => {
    const params = new URLSearchParams();
    for (const key of AXIS_KEYS) {
      const values = selected[key] ?? [];
      if (values.length > 0) params.set(key, values.join(","));
    }
    if (q.trim()) params.set("q", q.trim());
    if (limit !== PAGE_SIZE) params.set("limit", String(limit));
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  }, [selected, q, limit]);

  const toggle = (axis: string, slug: string) => {
    setLimit(PAGE_SIZE);
    setSelected((prev) => {
      const values = prev[axis] ?? [];
      const next = values.includes(slug) ? values.filter((s) => s !== slug) : [...values, slug];
      return { ...prev, [axis]: next };
    });
  };

  const applyBadgeFilter = (item: BadgeItem) => {
    toggle(meta.dimension.get(item.slug) ?? "activity", item.slug);
    window.requestAnimationFrame(() => {
      document.getElementById("companies-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearAll = () => {
    setSelected({ activity: [], industry: [], tech: [], model: [] });
    setQ("");
    setLimit(PAGE_SIZE);
  };

  const activeSlugSet = useMemo(() => {
    const set = new Set<string>();
    for (const key of AXIS_KEYS) {
      for (const slug of selected[key] ?? []) set.add(slug);
    }
    return set;
  }, [selected]);

  const chips = AXIS_KEYS.flatMap((axis) =>
    (selected[axis] ?? []).map((slug) => ({ axis, slug, name: meta.name.get(slug) ?? slug }))
  );
  const activeCount = chips.length;
  const hasFilters = activeCount > 0 || q.trim().length > 0;

  const isRu = locale === "ru";
  const labels = {
    title: isRu ? "Каталог компаний" : "Companies",
    subtitle: isRu ? "IT-компании, создающие инновационные решения" : "IT companies creating innovative solutions",
    search: isRu ? "Поиск компании…" : "Search companies…",
    filters: isRu ? "Фильтры" : "Filters",
    reset: isRu ? "Сбросить" : "Reset",
    resetAll: isRu ? "Сбросить всё" : "Reset all",
    details: isRu ? "Подробнее" : "Details",
    website: isRu ? "Сайт" : "Website",
    showMore: isRu ? "Показать ещё" : "Show more",
    found: isRu ? "Найдено" : "Found",
    total: isRu ? "компаний" : "companies",
    noMatch: isRu ? "Ничего не найдено" : "Nothing found",
    noMatchHint: isRu ? "Попробуйте изменить фильтры или поисковый запрос" : "Try changing filters or your search query",
    empty: isRu ? "Компании пока не добавлены" : "No companies yet",
  };

  const counter =
    filtered.length === companies.length
      ? `${companies.length} ${labels.total}`
      : `${filtered.length} ${isRu ? "из" : "of"} ${companies.length}`;

  return (
    <div>
      <PageHero
        id="companies-hero"
        title={labels.title}
        description={labels.subtitle}
        actions={[
          {
            href: `/${locale}/book-call?source=companies`,
            label: isRu ? "Обсудить проект" : "Discuss a project",
            primary: true,
            ctaLocation: "pagehero_companies_primary",
          },
        ]}
      >
        {companies.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setLimit(PAGE_SIZE);
                }}
                placeholder={labels.search}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-2.5 pl-10 pr-4 text-sm text-white placeholder-white/40 transition-colors focus:border-blue-400/50 focus:bg-white/[0.06] focus:outline-none"
              />
              <svg
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
            </div>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                open || activeCount > 0
                  ? "border-blue-400/40 bg-blue-500/15 text-blue-200"
                  : "border-white/10 bg-white/[0.04] text-white/80 hover:bg-white/[0.08]"
              }`}
            >
              {labels.filters}
              {activeCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-400/25 px-1.5 text-xs text-blue-100">
                  {activeCount}
                </span>
              )}
              <span className={`text-white/40 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
            </button>

            {hasFilters && (
              <button
                type="button"
                onClick={clearAll}
                className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/[0.08]"
              >
                {labels.reset}
              </button>
            )}

            <div className="ml-auto text-sm text-white/50">{counter}</div>
          </div>
        )}
      </PageHero>

      <div className="mb-6">
        <CompanyMatcher locale={locale} />
      </div>

      {companies.length > 0 && (
        <div className="mb-6">
          <div className="mt-3 flex h-9 items-center gap-2 overflow-x-auto whitespace-nowrap">
            {chips.map((chip) => (
              <button
                key={`${chip.axis}-${chip.slug}`}
                type="button"
                onClick={() => toggle(chip.axis, chip.slug)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs text-white/80 transition-colors hover:bg-white/[0.12]"
              >
                {chip.name}
                <span className="text-white/40">×</span>
              </button>
            ))}
          </div>

          {open && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md">
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {axes.map((axis) => (
                  <div key={axis.slug} className="min-w-0">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                      {axis.name}
                    </div>
                    <div className="max-h-72 space-y-0.5 overflow-y-auto pr-1">
                      {axis.groups.map((group) => (
                        <div key={group.slug}>
                          <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/[0.06]">
                            <input
                              type="checkbox"
                              checked={(selected[axis.slug] ?? []).includes(group.slug)}
                              onChange={() => toggle(axis.slug, group.slug)}
                              className="h-4 w-4 shrink-0 rounded border-white/20 bg-gray-800 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="truncate text-sm font-medium text-white/90">{group.name}</span>
                          </label>
                          {group.children.length > 0 && (
                            <div className="ml-6 space-y-0.5">
                              {group.children.map((leaf) => (
                                <label
                                  key={leaf.slug}
                                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-white/[0.06]"
                                >
                                  <input
                                    type="checkbox"
                                    checked={(selected[axis.slug] ?? []).includes(leaf.slug)}
                                    onChange={() => toggle(axis.slug, leaf.slug)}
                                    className="h-4 w-4 shrink-0 rounded border-white/20 bg-gray-800 text-blue-500 focus:ring-blue-500"
                                  />
                                  <span className="truncate text-[13px] text-white/60">{leaf.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-white/10 pt-3">
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={!hasFilters}
                  className="rounded-lg px-3 py-1.5 text-sm text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white/90 disabled:opacity-40"
                >
                  {labels.resetAll}
                </button>
                {activeCount > 1 && (
                  <p className="hidden text-xs text-white/40 sm:block">
                    {isRu
                      ? "Показаны компании, подходящие хотя бы под один из выбранных фильтров"
                      : "Showing companies matching at least one of the selected filters"}
                  </p>
                )}
                <span className="ml-auto text-sm text-white/50">{counter}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {companies.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 py-16 text-center">
          <p className="text-white/50">{labels.empty}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 py-16 text-center">
          <p className="text-lg font-medium text-white/80">{labels.noMatch}</p>
          <p className="mt-1 text-sm text-white/40">{labels.noMatchHint}</p>
        </div>
      ) : (
        <>
          <div id="companies-list" className="scroll-mt-24 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((company) => {
              const translation =
                company.translations.find((t) => t.locale === locale) ?? company.translations[0];
              const name = translation?.name || company.slug;
              const badgeItems: BadgeItem[] = company.categorySlugs
                .map((slug) => ({
                  key: slug,
                  slug,
                  name: meta.name.get(slug) ?? slug,
                  icon: meta.icon.get(slug) ?? null,
                  dimension: meta.dimension.get(slug) ?? null,
                }))
                .sort((a, b) => {
                  const ad = DIMENSION_ORDER[a.dimension ?? ""] ?? 9;
                  const bd = DIMENSION_ORDER[b.dimension ?? ""] ?? 9;
                  if (ad !== bd) return ad - bd;
                  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                });

              return (
                <article
                  key={company.id}
                  className="group relative flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-5 transition-all duration-200 hover:border-white/20 hover:bg-white/[0.07]"
                >
                  <Link
                    href={`/${locale}/companies/${company.slug}`}
                    className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
                    aria-label={name}
                  />

                  <div className="flex items-start gap-3">
                    {company.logo ? (
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-white/10">
                        <Image
                          src={company.logo}
                          alt={name}
                          width={44}
                          height={44}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/25 to-indigo-500/10 text-base font-semibold text-blue-200">
                        {name.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-white transition-colors group-hover:text-blue-300">
                        {name}
                      </h3>
                      {translation?.description && (
                        <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-white/50">
                          {translation.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex-1">
                    <CategoryBadgePills
                      items={badgeItems}
                      max={4}
                      className=""
                      selectedSlugs={activeSlugSet}
                      onSelect={applyBadgeFilter}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
                    <span className="text-[13px] font-medium text-blue-300/80 transition-colors group-hover:text-blue-300">
                      {labels.details} →
                    </span>
                    {company.website && (
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative z-10 inline-flex items-center gap-1.5 text-[13px] text-white/45 transition-colors hover:text-white/90"
                      >
                        {labels.website}
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setLimit((value) => value + PAGE_SIZE)}
                className="rounded-xl border border-white/15 bg-white/[0.06] px-6 py-3 text-sm font-medium text-white/85 transition-colors hover:bg-white/[0.12]"
              >
                {labels.showMore} {Math.min(PAGE_SIZE, filtered.length - visible.length)}
              </button>
            </div>
          )}
          {!hasMore && filtered.length > PAGE_SIZE && (
            <p className="mt-8 text-center text-sm text-white/40">
              {labels.found} {filtered.length}
            </p>
          )}
        </>
      )}
    </div>
  );
}